/**
 * PageErrorBoundary - Page-level error boundary with Sentry integration
 *
 * Lighter-weight boundary for individual pages/routes.
 * Includes route context in error reports.
 */

import * as Sentry from '@sentry/react';
import { useLocation } from 'react-router-dom';
import { ErrorFallback } from './ErrorFallback';

export interface PageErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional page name for error context */
  pageName?: string;
}

/**
 * Page-level error boundary
 * Provides route-specific error context to Sentry
 */
export function PageErrorBoundary({
  children,
  pageName,
}: PageErrorBoundaryProps): React.ReactElement {
  const location = useLocation();

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback
          error={error instanceof Error ? error : new Error(String(error))}
          resetError={resetError}
          title="Sidan kunde inte laddas"
        />
      )}
      showDialog={false}
      beforeCapture={(scope) => {
        scope.setTag('errorBoundary', 'page');
        scope.setTag('route', pageName || location.pathname);
        scope.setLevel('error');
        scope.setContext('page', {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        });
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export default PageErrorBoundary;
